import type { CMSAssetType } from '../../types';
import type { FileUploaderParams } from '../../hooks';
import type { NavigateFunction } from 'react-router';
import { Modal, ModalBody, ModalHeader, Row, Col, ModalFooter, Button, TabList } from '@workramp/ui';
import { useMemo, useState } from 'react';
import { I18n } from '~/common/explicit-exports';
import { CMS_BASE_URL } from '../../constants';
import { useFileUploader } from '../../hooks';
import { isUrlAndDomainValid } from '../../utils/url';
import { useCreateLink } from '../../api';
import { CMSNewLinkAssetModalTab } from './CMSNewLinkAssetModalTab';
import { CMSNewFileAssetModalTab } from './CMSNewFileAssetModalTab';

export type CMSNewAssetModalProps = {
  isOpen: boolean;
  navigate?: NavigateFunction;
  onClose: () => void;
  onSave: (type: CMSAssetType, value: string | File) => void;
};

const { APP_CONSTANTS } = window;
const { CMS_ASSET_TYPES } = APP_CONSTANTS;

export const CMSNewAssetModal = ({ isOpen, navigate, onClose, onSave }: CMSNewAssetModalProps) => {
  const TRANSLATED_FILE = I18n.t('js.admin.cms.file');
  const TRANSLATED_LINK = I18n.t('js.admin.cms.link');
  const TRANSLATED_CANCEL = I18n.t('js.admin.cms.cancel');
  const TRANSLATED_TITLE = I18n.t('js.admin.cms.new_asset_modal_title');
  const TRANSLATED_ADD = I18n.t('js.admin.cms.new_asset_modal_title');
  const TRANSLATED_UPLOADING = I18n.t('js.admin.cms.uploading');
  const TRANSLATED_UPLOAD_FAILED = I18n.t('js.admin.cms.upload_failed');

  const [fileValue, setFileValue] = useState<File>(null);
  const [urlValue, setUrlValue] = useState('');
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [selectedTabKey, setSelectedTabKey] = useState<string | number>(CMS_ASSET_TYPES.FILE);
  const [isUploadingLink, setIsUploadingLink] = useState(false);

  const createLink = useCreateLink();

  const clearModalInput = () => {
    setFileValue(null);
    setUrlValue('');
  };

  const handleModalClose = () => {
    clearModalInput();
    onClose();
  };

  const fileUploadEventHandlers: FileUploaderParams = {
    onUploadSuccess: ({ assetId }) => {
      navigate?.(`${CMS_BASE_URL}/file/${assetId}`);
      onSave(CMS_ASSET_TYPES.FILE, fileValue);
    },
    onUploadFailed: () => setWarningMessage(TRANSLATED_UPLOAD_FAILED),
    afterUpload: clearModalInput,
  };

  const linkUploadHandler = {
    onSuccess: async (data) => {
      setIsUploadingLink(false);
      navigate?.(`${CMS_BASE_URL}/link/${data.assetId}`);
      onSave(CMS_ASSET_TYPES.LINK, urlValue);
      clearModalInput();
    },
    onError: () => {
      setIsUploadingLink(false);
      setWarningMessage(TRANSLATED_UPLOAD_FAILED);
    },
  };

  const { isUploading, uploadFile } = useFileUploader(fileUploadEventHandlers);

  const isValidCMSLink = (url: string) => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return isUrlAndDomainValid(url, ['https', 'http']);
    }
    return isUrlAndDomainValid(`https://${url}`, ['https', 'http']);
  };

  const isAddAssetButtonDisabled = useMemo(() => {
    if (isUploading || isUploadingLink) {
      return true;
    }

    if (selectedTabKey === CMS_ASSET_TYPES.FILE) {
      return !fileValue;
    }
    return !isValidCMSLink(urlValue);
  }, [fileValue, urlValue, selectedTabKey, isUploading, isUploadingLink]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={isUploading || isUploadingLink ? null : handleModalClose}
      isDismissable={!isUploading && !isUploadingLink}
    >
      <ModalHeader title={TRANSLATED_TITLE} />
      <ModalBody>
        <TabList
          aria-label="New CMS asset type"
          selectedKey={selectedTabKey}
          onSelectionChange={(key) => {
            setWarningMessage('');
            setSelectedTabKey(key);
          }}
        >
          <TabList.Item key={CMS_ASSET_TYPES.FILE} title={TRANSLATED_FILE}>
            <CMSNewFileAssetModalTab
              isUploading={isUploading}
              fileValue={fileValue}
              setFileValue={setFileValue}
              warningMessage={warningMessage}
              setWarningMessage={setWarningMessage}
            />
          </TabList.Item>
          <TabList.Item key={CMS_ASSET_TYPES.LINK} title={TRANSLATED_LINK} data-testid="tab-button-link">
            <CMSNewLinkAssetModalTab
              urlValue={urlValue}
              setUrlValue={setUrlValue}
              warningMessage={warningMessage}
              isUploadingLink={isUploadingLink}
            />
          </TabList.Item>
        </TabList>
      </ModalBody>
      <ModalFooter>
        <Row justify="flex-end" gapX={2}>
          <Col xs="auto">
            {!isUploading && !isUploadingLink && (
              <Button
                data-testid="button-cancel"
                color="secondary"
                variant="text"
                onPress={() => {
                  clearModalInput();
                  onClose();
                }}
              >
                {TRANSLATED_CANCEL}
              </Button>
            )}
          </Col>
          <Col xs="auto">
            <Button
              data-testid="button-save"
              color="primary"
              variant="solid"
              isDisabled={isAddAssetButtonDisabled}
              onPress={() => {
                if (selectedTabKey === CMS_ASSET_TYPES.FILE && fileValue) {
                  uploadFile(fileValue);
                } else if (selectedTabKey === CMS_ASSET_TYPES.LINK && urlValue) {
                  setIsUploadingLink(true);
                  createLink.mutate({ url: urlValue }, linkUploadHandler);
                }
              }}
            >
              {isUploading || isUploadingLink ? TRANSLATED_UPLOADING : TRANSLATED_ADD}
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
